/**
 * =============================================================================
 * ADMIN APP — session gate + release-flag surface selection (ADR-025 B-1)
 * =============================================================================
 *
 * The component `main.tsx` thin-mounts (inside the provider stack). Owns, in
 * order:
 *
 *   1. POST-INSTALL HANDOFF (preserved): `/api/oauth/auth` redirects here with
 *      `installed=true&store=…` and NO JWT — render the success screen, not a
 *      login prompt.
 *   2. THE M1 AUTH GATE: no token → ONE clean "open from BigCommerce" screen
 *      and ZERO network requests (the release-flag read is token-gated below,
 *      and no panel mounts) — replacing the legacy ragged tokenless
 *      multi-panel fallback for both surfaces.
 *   3. RELEASE-FLAG SURFACE SELECTION (§3.5 / M17): the server-evaluated
 *      `adminWorkbenchShell` flag picks EXACTLY ONE mounted surface —
 *      ON → the B-1 `AdminShell`; OFF / unreadable / malformed → the legacy
 *      flat layout (fail-closed; the prior surface stays one flip away).
 *      While the read is in flight a neutral loading screen renders so the
 *      wrong surface is never mounted-then-swapped (no double startup
 *      fetches, no flash).
 *
 * STARTUP FETCH INVENTORY (M14 mechanism — measured budget rides E4):
 *   unauthenticated: 0 requests.
 *   flag ON  (shell): 1× shared settings + 1× pending work + 1× minimal
 *     launch status + 1× Questions landing = 4 requests.
 *   flag OFF (legacy): 1× shared settings + the unchanged legacy Analytics
 *     and Questions reads = 3 requests. Both settings cards consume the
 *     shared cache on either surface and never add private settings GETs.
 * =============================================================================
 */

import React from 'react';
import { Box, Text } from '@bigcommerce/big-design';
import { useSession } from '../../hooks/useSession';
import { useReleaseFlags } from '../../hooks/useReleaseFlags';
import { TenantSettingsProvider } from '../../providers/TenantSettingsProvider';
import {
  InstalledSuccessScreen,
  isPostInstallRedirect,
  postInstallStoreHash,
} from '../PostInstall';
import { AuthGate } from './AuthGate';
import { AdminShell } from './AdminShell';
import { LegacyAdminLayout } from './LegacyAdminLayout';

/**
 * Authenticated half — a separate component so the release-flag hook only
 * ever runs (and can only ever fetch) with a live session.
 */
function AuthenticatedAdmin({ token }: { token: string }): React.ReactElement {
  const { status, flags } = useReleaseFlags(token);

  if (status !== 'ready') {
    return (
      <Box padding="large" data-testid="admin-app-loading">
        <Text color="secondary">Loading…</Text>
      </Box>
    );
  }

  // §3.5: one implementation mounted per surface — the flag selects the entry.
  return flags.adminWorkbenchShell ? (
    <AdminShell questionWorkspaceEnabled={flags.adminQuestionWorkspace} />
  ) : (
    <LegacyAdminLayout />
  );
}

export function AdminApp(): React.ReactElement {
  const { token, isAuthenticated } = useSession();

  // Post-install handoff: BigCommerce hands us a JWT later, when the merchant
  // opens the app from the control panel via `/api/oauth/load`. Without a
  // token the dashboard cannot make authenticated calls, so render an
  // explicit "you're done — open the app from BC next" success screen.
  if (!isAuthenticated && isPostInstallRedirect()) {
    return <InstalledSuccessScreen storeHash={postInstallStoreHash()} />;
  }

  if (!isAuthenticated || !token) {
    return <AuthGate />;
  }

  return (
    <TenantSettingsProvider>
      <AuthenticatedAdmin token={token} />
    </TenantSettingsProvider>
  );
}
